import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH9_QUESTIONS } from "../data/neetPhysicsCh9Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 9: Mechanical Properties of Solids (NCERT Class 11).
// Content is drafted from the NCERT syllabus topic list; each concept has an
// explanation plus a worked example. NOT YET human-reviewed by a subject-matter
// expert — treat as a first draft and verify against NCERT before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Stress & Strain", "Elastic Moduli", "Stress-Strain Curve & Elastic Energy"];

const SECTION_COLORS = {
  "Stress & Strain":                          "#0891b2",
  "Elastic Moduli":                            "#ca8a04",
  "Stress-Strain Curve & Elastic Energy":      "#be185d",
};

const CONCEPTS = [
  // ── Stress & Strain ─────────────────────────────────────────────────────────
  { section: "Stress & Strain", title: "Elasticity, plasticity and Hooke's Law",
    explanation: "Deforming forces change the size and/or shape of a body. A body that returns exactly to its original size and shape once the deforming force is removed is called perfectly elastic (quartz and phosphor bronze come close to this ideal). A body that stays deformed and shows little tendency to recover is called plastic (putty and wet clay are common examples). Hooke's law states that, within the elastic limit, the stress developed in a body is directly proportional to the strain produced: stress = k x strain, where the constant k is the modulus of elasticity of the material -- it depends only on the material, not on the object's size or shape.",
    example: `Stress = k x Strain     (Hooke's Law -- valid only within the elastic limit)
k = modulus of elasticity (SI unit: pascal, Pa -- same unit as stress)

Nearly perfectly elastic: quartz, phosphor bronze
Nearly plastic (little recovery): putty, wet clay` },
  { section: "Stress & Strain", title: "Stress and its three types",
    explanation: "Stress is the internal restoring force set up per unit cross-sectional area of a body when it is deformed: Stress = F / A, with SI unit pascal (Pa = N/m^2) -- the same unit as pressure. Depending on how the deforming force acts relative to the surface, three types arise: (1) Longitudinal (normal) stress -- force perpendicular to the area, tensile if it stretches the body, compressive if it compresses it; (2) Shearing (tangential) stress -- force parallel to the surface, changing the body's shape without changing its volume; (3) Volume (hydraulic) stress -- a uniform force perpendicular to the entire surface from all sides (as by fluid pressure), changing only the volume, not the shape.",
    example: `Stress = F / A            (unit: Pa = N/m^2, same dimensions as pressure)

Longitudinal stress    -> tensile (pulling) or compressive (pushing)
Shearing stress        -> tangential force, changes shape only
Volume/hydraulic stress-> uniform normal force from all sides, changes volume only

A rod pulled at both ends by F = 500 N, cross-section A = 2x10^-4 m^2:
Tensile stress = F/A = 500 / (2x10^-4) = 2.5x10^6 Pa` },
  { section: "Stress & Strain", title: "Strain and its three types",
    explanation: "Strain is the fractional change produced in a body's dimensions by a deforming force. Being a ratio of two like quantities, it is dimensionless. Matching the three stress types: (1) Longitudinal strain = change in length / original length = dL/L; (2) Shearing strain = the angle theta (in radians) through which a face tilts, approximated for small angles as theta ~ dx/L, where dx is the relative lateral displacement between two faces separated by perpendicular distance L; (3) Volume strain = change in volume / original volume = dV/V.",
    example: `Longitudinal strain = dL / L          (dimensionless)
Shearing strain      = theta ~ dx / L  (radians, dimensionless)
Volume strain         = dV / V          (dimensionless)

A wire of original length L = 2.000 m stretches to 2.0015 m:
Longitudinal strain = 0.0015 / 2.000 = 7.5x10^-4` },
  { section: "Stress & Strain", title: "Longitudinal stress-strain — tensile and compressive",
    explanation: "When equal and opposite forces act perpendicular to a wire's or rod's cross-section along its length, tending to increase its length, the internal stress is TENSILE; if the forces tend to decrease its length, the stress is COMPRESSIVE. In both cases stress = F/A (longitudinal stress) and strain = dL/L (longitudinal strain). A wire hanging a weight, or a pillar supporting a load from above, are the standard examples used to compute these before applying Young's modulus.",
    example: `A steel wire, radius r = 0.5 mm, supports a hanging load of F = 78.5 N.
A = pi.r^2 = pi x (0.5x10^-3)^2 = 7.85x10^-7 m^2
Tensile stress = F/A = 78.5 / 7.85x10^-7 = 1.0x10^8 Pa` },
  { section: "Stress & Strain", title: "Shearing stress and shearing strain",
    explanation: "Shearing stress arises when equal and opposite forces act tangentially (parallel) to a surface, making one layer of the body slide relative to an adjacent layer -- changing its shape (e.g. rectangle to parallelogram) while its volume stays essentially unchanged. Shearing stress = tangential force / area of the sheared face; shearing strain = dx/L, the ratio of the relative lateral displacement dx of the sheared face to the perpendicular distance L between the fixed and sheared faces.",
    example: `A block of side L = 0.10 m, top face area A = 0.02 m^2.
A tangential force F = 40 N shifts the top face by dx = 2x10^-4 m.

Shearing stress = F/A = 40/0.02 = 2000 Pa
Shearing strain = dx/L = 2x10^-4 / 0.10 = 2x10^-3   (dimensionless)` },
  { section: "Stress & Strain", title: "Volume (hydraulic) stress and strain",
    explanation: "When a body is subjected to a uniform pressure acting perpendicular to its surface at every point (as inside a fluid), the resulting stress is called volume or hydraulic stress, numerically equal to the increase in pressure, dp. This tends to compress the body uniformly, changing its volume without changing its shape. The corresponding strain, volume strain, is the fractional change in volume, dV/V. Fluids cannot sustain shearing stress (they flow instead), but they can sustain volume stress -- which is why liquids and gases have a bulk modulus but no shear modulus or Young's modulus.",
    example: `Volume stress = change in pressure, dp     (Pa)
Volume strain = dV / V                      (dimensionless)

A solid ball of volume V = 1.0x10^-3 m^3 is lowered into the sea where
pressure increases by dp = 1.0x10^7 Pa, and its volume decreases by
dV = 4.5x10^-6 m^3.
Volume strain = dV/V = 4.5x10^-6 / 1.0x10^-3 = 4.5x10^-3` },

  // ── Elastic Moduli ───────────────────────────────────────────────────────────
  { section: "Elastic Moduli", title: "Young's modulus — definition and worked example",
    explanation: "Young's modulus (Y) is the modulus of elasticity for longitudinal stress and strain: Y = longitudinal stress / longitudinal strain = (F/A) / (dL/L) = F.L / (A.dL), SI unit pascal. It measures a solid's resistance to being stretched or compressed along its length -- the larger Y, the stiffer the material. Typical solids have Y of order 10^9-10^11 Pa (e.g. steel ~ 2.0x10^11 Pa, aluminium ~ 7.0x10^10 Pa), while rubber has Y of only about 10^6 Pa -- far more easily stretched than any metal.",
    example: `Y = (F/A) / (dL/L) = F.L / (A.dL)

A steel wire, L = 2 m, A = 1.0x10^-6 m^2, is pulled by F = 100 N.
Given Y(steel) = 2.0x10^11 Pa, find the elongation dL.

dL = F.L / (A.Y) = (100 x 2) / (1.0x10^-6 x 2.0x10^11)
   = 200 / (2.0x10^5) = 1.0x10^-3 m = 1 mm` },
  { section: "Elastic Moduli", title: "Shear modulus (modulus of rigidity) — definition and worked example",
    explanation: "The shear modulus (G, also called the modulus of rigidity) is the modulus of elasticity for shearing stress and strain: G = shearing stress / shearing strain = (F/A) / theta, SI unit pascal. It measures a solid's resistance to a change of SHAPE at (essentially) constant volume. For any given material, G is always smaller than that material's Young's modulus Y -- solids resist a stretch more strongly than they resist a shape-distorting shear. Fluids have no shear modulus at all, since they offer no resistance to a change of shape.",
    example: `G = shearing stress / shearing strain = (F/A) / theta

A cube of side L = 0.20 m has its top face (area A = 0.04 m^2) sheared
by F = 800 N, producing a lateral displacement dx = 4.0x10^-5 m.

Shearing stress = F/A = 800/0.04 = 2.0x10^4 Pa
Shearing strain = dx/L = 4.0x10^-5/0.20 = 2.0x10^-4
G = stress/strain = 2.0x10^4 / 2.0x10^-4 = 1.0x10^8 Pa` },
  { section: "Elastic Moduli", title: "Bulk modulus and compressibility — definition and worked example",
    explanation: "The bulk modulus (B) is the modulus of elasticity for volume stress and strain: B = -dp / (dV/V), SI unit pascal. The negative sign shows that an increase in pressure (dp positive) always produces a decrease in volume (dV negative), keeping B itself positive. Its reciprocal, k = 1/B, is called compressibility. Solids have the largest B (least compressible), liquids intermediate, and gases the smallest B (most compressible) -- e.g. B(steel) ~ 1.6x10^11 Pa, B(water) ~ 2.2x10^9 Pa, B(air, atmospheric) ~ 1.0x10^5 Pa.",
    example: `B = -dp / (dV/V)          k = 1/B  (compressibility)

Water (B = 2.2x10^9 Pa) is compressed with a pressure increase of
dp = 1.0x10^7 Pa (~100 atmospheres). Find the fractional decrease in volume.

dV/V = dp/B = 1.0x10^7 / 2.2x10^9 = 4.5x10^-3   (i.e. ~0.45% decrease)` },
  { section: "Elastic Moduli", title: "Poisson's ratio",
    explanation: "When a wire is stretched, it doesn't just get longer -- it also gets very slightly thinner (its lateral dimension shrinks); the reverse happens under compression. Poisson's ratio (sigma) is defined as the negative of the ratio of lateral strain to longitudinal strain: sigma = -(lateral strain)/(longitudinal strain), a dimensionless number. For most engineering materials sigma lies roughly between 0.2 and 0.4; for an isotropic elastic material it must theoretically lie between -1 and 0.5. It is not fully independent of Y, G and B -- for an isotropic solid the elastic constants are interrelated, so only two of Y, G, B, sigma are truly independent.",
    example: `Poisson's ratio, sigma = -(lateral strain) / (longitudinal strain)

A wire of radius 1.00 mm and length 2.0 m is stretched so that:
Longitudinal strain    = 1.0x10^-3
Lateral (radius) strain = -3.0x10^-4   (radius decreases)

sigma = -(-3.0x10^-4) / (1.0x10^-3) = 0.30   (typical for metals)` },
  { section: "Elastic Moduli", title: "Comparing elastic moduli of common materials",
    explanation: "Metals like steel have very high elastic moduli (stiff, hard to deform), while rubber-like elastomers have moduli roughly 10^4-10^5 times smaller (easily stretched). For a typical isotropic solid, the modulus resisting a change in length or volume (Y, B) is generally much larger than the modulus resisting a pure change in shape (G) -- solids are comparatively 'floppier' in shear than in stretch or compression. This is why steel is chosen for structural rigidity (bridges, rails, machine parts) while rubber is chosen where large, recoverable deformation is wanted (tyres, gaskets, shock mounts).",
    example: `Approximate elastic moduli (all values in units of 10^10 Pa):
Material     Y (Young's)   G (Shear)   B (Bulk)
Steel           20            8.4         16
Aluminium        7            2.5          7.2
Copper          12            4.4         14
Rubber      ~0.0001-0.001  ~0.00003-0.0003  ~0.02-0.03

-> Steel is roughly 10,000 to 100,000 times stiffer than rubber` },

  // ── Stress-Strain Curve & Elastic Energy ────────────────────────────────────
  { section: "Stress-Strain Curve & Elastic Energy", title: "Stress-strain curve — proportional limit and elastic limit",
    explanation: "Plotting stress (vertical axis) against strain (horizontal axis) as a wire is gradually loaded gives the stress-strain curve. From the origin O up to a point A, the curve is a straight line and stress is proportional to strain -- Hooke's law holds, and A is the proportional limit. Beyond A, up to a nearby point B (the elastic limit / yield point), stress and strain are no longer proportional, but the material still returns to its original dimensions if the load is removed. Beyond B the material enters the PLASTIC region -- a small increase in stress now produces a disproportionately large strain, and removing the load no longer restores the original length (a permanent 'set' remains).",
    example: `Stress
  |                          D (ultimate tensile stress)
  |                        .-'\\
  |                     .-'     \\
  |         B _______.-'          \\  E (fracture)
  |         /|                      \\.
  |        / |
  |       /  |   <- plastic region (beyond elastic limit)
  |      A   |
  |     /|   |
  |    / |<- elastic region (Hooke's law: O to A)
  |   /  |
  |  /___|______________________________________ Strain
  O

O-A : proportional limit  (stress proportional to strain)
A-B : still elastic, but no longer proportional
B->onward: plastic deformation begins (permanent set on unloading)` },
  { section: "Stress-Strain Curve & Elastic Energy", title: "Ultimate tensile stress and fracture point",
    explanation: "Beyond the yield point, further stretching needs progressively less additional stress to produce large strain, until the curve reaches its highest point D -- the ultimate tensile stress (tensile strength): the maximum stress the material can withstand before failure begins. Just past D, the wire develops a local 'neck' (reduced cross-section), and the (engineering) stress appears to fall even as the true local stress keeps rising, until the wire snaps at point E, the fracture point. Ductile materials (e.g. mild steel) show a long plastic region between yield and fracture; brittle materials (e.g. glass, cast iron) fracture soon after the elastic limit, with little or no plastic deformation.",
    example: `Ductile material (e.g. mild steel): long gap between yield point and
fracture point -> large plastic deformation (visible stretching/necking)
before it finally breaks.

Brittle material (e.g. glass, cast iron): fracture point lies very
close to the elastic limit -> breaks suddenly with almost no
plastic/ductile region and little visible warning.` },
  { section: "Stress-Strain Curve & Elastic Energy", title: "Elastic potential energy stored in a stretched wire",
    explanation: "Work must be done against a wire's internal restoring (elastic) force to stretch it, and this work is stored as elastic potential energy. For a wire obeying Hooke's law, the tension rises linearly from 0 to F as the wire stretches from 0 to dL, so the energy stored per unit volume equals (1/2) x stress x strain, and the total elastic potential energy is U = (1/2) x stress x strain x volume. Equivalently, in terms of force and elongation, U = (1/2).F.dL -- the same form as the energy stored in a stretched ideal spring.",
    example: `Energy density = (1/2) x stress x strain
U = (1/2) x stress x strain x Volume  =  (1/2) x F x dL

Using the steel wire from the Young's modulus example:
F = 100 N, dL = 1.0x10^-3 m, A = 1.0x10^-6 m^2, L = 2 m

U = (1/2) x F x dL = (1/2)(100)(1.0x10^-3) = 0.05 J

Check via the stress-strain-volume form:
stress = F/A = 1.0x10^8 Pa,  strain = dL/L = 5.0x10^-4,  Volume = A.L = 2.0x10^-6 m^3
U = (1/2)(1.0x10^8)(5.0x10^-4)(2.0x10^-6) = 0.05 J   (matches)` },
  { section: "Stress-Strain Curve & Elastic Energy", title: "Elastomers and elastic hysteresis",
    explanation: "Materials such as rubber, which can be stretched to many times their original length and still return to nearly their original shape, are called elastomers. Their stress-strain curve is markedly non-linear -- they never obey Hooke's law, even at small strains -- and unlike a metal wire, the loading curve (stretching) and the unloading curve (releasing) don't retrace the same path; together they enclose a loop called the elastic hysteresis loop. The area enclosed by this loop equals the energy dissipated as heat within the material during one stretch-release cycle, which is why rubber that is repeatedly flexed (like a tyre rolling on a road) warms up.",
    example: `Loading curve (stretching) does NOT coincide with the
Unloading curve (releasing) for an elastomer like rubber.

Area enclosed by the loading + unloading curves
   = energy dissipated as heat per cycle
   (this is why a rubber band feels warm after being stretched
    and released rapidly, many times in succession)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh9Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh9Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh9Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH9_QUESTIONS : NEET_PHYSICS_CH9_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 9</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Mechanical Properties of Solids</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Ch. 9 · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH9_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH9_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
